import React from "react";
import LineChart from "components/shared/Charts/LineChart";
import { Col, Card, Spinner, CardHeader, Row, CardBody } from "reactstrap";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { RepositoryFactory } from "repository/RepositoryFactory";

const Stats = RepositoryFactory.get("stats");

const generateCategories = (type, dates) => {
  if (type === "day") {
    const categories = [];
    for (let i = 0; i < 24; i += 2) {
      const start = dayjs().hour(i).minute(0).format("hA");
      const end = dayjs()
        .hour(i + 2)
        .minute(0)
        .format("hA");
      categories.push(`${start} - ${end}`);
    }
    return categories;
  } else if (type === "month") {
    const today = dayjs();
    const totalDays = today.daysInMonth();
    return Array.from({ length: totalDays }, (_, i) => i + 1);
  } else if (type === "year") {
    return [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
  } else if (type === "range") {
    const { startDate, endDate } = dates;
    if (!startDate || !endDate) return [];
    let start = dayjs(startDate, "DD-MM-YYYY");
    const end = dayjs(endDate, "DD-MM-YYYY");

    const categories = [];
    while (start.isBefore(end) || start.isSame(end)) {
      categories.push(start.format("DD-MM-YYYY"));
      start = start.add(1, "day");
    }
    return categories;
  }
  return [];
};

const OrderAnalysis = ({ type, dates, merchant }) => {
  const { startDate, endDate } = dates;
  const { user } = useSelector((state) => state.auth);

  const payload = {
    type,
    dateRange: { startDate, endDate },
    id:
      user?.type === "restaurant"
        ? user?.restaurantID
        : user?.type === "admin" && merchant?.id
        ? merchant?.id
        : "",
  };

  const {
    data: latestOrderAnalysis,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["orderAnalysis", payload],
    queryFn: async () => {
      const { data } = await Stats.getOrderAnalysis(payload);
      return Object.values(data?.data || {});
    },
    staleTime: 60 * 60 * 1000,
    cacheTime: 70 * 60 * 1000,
    keepPreviousData: true,
  });

  if (isError) {
    toast.error(error?.message || "Failed to load order analysis.");
  }

  const options = {
    chart: {
      id: "basic-line",
      toolbar: { show: false },
      style: { fontFamily: "poppins" },
    },
    colors: ["#2DCE89", "#F5365C", "#FFD600"],
    xaxis: {
      categories: generateCategories(type, dates),
      labels: {
        style: { fontFamily: "Poppins, sans-serif" },
      },
    },
    stroke: { curve: "smooth" },
    legend: { show: false },
  };

  const series = [
    {
      name: "Completed",
      data: latestOrderAnalysis?.map((order) => order?.completed) || [],
    },
    {
      name: "Refunded",
      data: latestOrderAnalysis?.map((order) => order?.refunded) || [],
    },
    {
      name: "In-Progress",
      data: latestOrderAnalysis?.map((order) => order?.pending) || [],
    },
  ];

  return (
    <Row>
      <Col md={12}>
        <Card className="header-cards card overflow-hidden">
          <CardHeader className="d-flex justify-content-between flex-wrap flex-column flex-sm-row">
            <h3 className="mb-0 text-nowrap">Order Analysis</h3>
            <div className="d-flex align-items-center mt-2 mt-sm-0 flex-wrap">
              <div className="d-flex align-items-center">
                <div
                  style={{ width: "10px", height: "10px" }}
                  className="bg-success rounded-circle mr-2"
                />
                <span className="fs-12 mr-3">Completed</span>
              </div>
              <div className="d-flex align-items-center">
                <div
                  style={{ width: "10px", height: "10px" }}
                  className="bg-danger rounded-circle mr-2"
                />
                <span className="fs-12 mr-3">Refunded</span>
              </div>
              <div className="d-flex align-items-center">
                <div
                  style={{ width: "10px", height: "10px" }}
                  className="bg-yellow rounded-circle mr-2"
                />
                <span className="fs-12 text-nowrap">In-Progress</span>
              </div>
            </div>
          </CardHeader>
          {isFetching ? (
            <Spinner size="sm" color="primary" className="mx-auto my-5" />
          ) : (
            <CardBody>
              <LineChart options={options} series={series} />
            </CardBody>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default OrderAnalysis;
